import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, Trophy, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CompetitionPopup = () => {
  const [show, setShow] = useState(false);
  const [status, setStatus] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname !== '/') {
      setShow(false);
      return undefined;
    }

    const dismissed = sessionStorage.getItem('competition_popup_dismissed');
    if (dismissed) return;

    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/api/competition/status`);
        if (res.ok) {
          const data = await res.json();
          if (data.active) {
            setStatus(data);
            setShow(true);
          }
        }
      } catch (e) {
        // silently fail
      }
    }, 4000);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  const handleClose = () => {
    setShow(false);
    sessionStorage.setItem('competition_popup_dismissed', 'true');
  };

  const handleParticipate = () => {
    handleClose();
    navigate('/wettbewerb');
  };

  if (!show || !status || location.pathname !== '/') return null;

  return (
    <AnimatePresence>
      {show && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={handleClose}
          />
          {/* Popup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 40 }}
            transition={{ type: 'spring', duration: 0.5 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4 pointer-events-none"
          >
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden pointer-events-auto" data-testid="competition-popup">
              {/* Close */}
              <button
                onClick={handleClose}
                className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/10 hover:bg-black/20 transition-colors"
                data-testid="competition-popup-close"
              >
                <X className="w-4 h-4 text-white" />
              </button>

              {/* Header */}
              <div className="bg-gradient-to-br from-[#1D5CA5] to-[#3B82F6] px-6 py-8 text-center text-white">
                <div className="inline-flex items-center gap-1.5 bg-white/20 px-3 py-1 rounded-full text-xs font-medium mb-4">
                  <Sparkles className="w-3 h-3" />
                  Wettbewerb aktiv
                </div>
                <h2 className="text-2xl font-bold mb-2">1 Woche GRATIS</h2>
                <p className="text-sm text-blue-100">Werbefläche gewinnen!</p>
              </div>

              {/* Body */}
              <div className="px-6 py-6 space-y-4">
                {status.last_winner_name && (
                  <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                    <Trophy className="w-6 h-6 text-amber-500 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-amber-600 font-medium">Letzter Gewinner</p>
                      <p className="text-sm font-bold text-amber-800">{status.last_winner_name}</p>
                    </div>
                  </div>
                )}

                <div className="space-y-2 text-center">
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">
                    Melden Sie sich jetzt an und gewinnen Sie eine Woche <strong>kostenlose Werbefläche</strong>.
                    Ab 100 Teilnehmern wird automatisch ein Gewinner ausgelost!
                  </p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    Bereits <strong>{status.total_participants}</strong> Teilnehmer registriert
                  </p>
                </div>

                <Button
                  onClick={handleParticipate}
                  className="w-full"
                  size="lg"
                  data-testid="competition-popup-cta"
                >
                  <Trophy className="w-4 h-4 mr-2" />
                  Jetzt teilnehmen
                </Button>

                <button
                  onClick={handleClose}
                  className="w-full text-center text-xs text-[hsl(var(--muted-foreground))] hover:underline"
                  data-testid="competition-popup-dismiss"
                >
                  Nein, danke
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CompetitionPopup;
